import { inject, Injectable } from '@angular/core';

import { AppInsightsBaseService } from '@kros-sk/core/application-insights';

/** Which variant of the marketing header the recipient was actually shown. */
export type DocumentViewHeaderVariant = 'trialAndLogin' | 'appAndAccount' | 'none';

/**
 * Application Insights events the public document viewer reports from code: the page load, what the header and the
 * offer showed, and the clicks of the components outside the schema. A click of a schema-built tile is a `track-event`
 * declared in the schema itself (`received-invoice-public-document.json`), which names the event beside the action it
 * accompanies; `public-document-schema.spec.ts` holds those names to the known list.
 *
 * No PII is logged — only neutral metadata (document type, click source, failure reason).
 * The underlying {@link AppInsightsBaseService} resolves to the real or no-op implementation
 * provided at app level (`provideAppInsights` / `provideNoopAppInsights`).
 */
@Injectable({ providedIn: 'root' })
export class DocumentViewTelemetryService {
  readonly #appInsights = inject(AppInsightsBaseService);

  #documentId: string | null = null;

  readonly #reportedOnce = new Set<string>();

  /**
   * Internal id from the link response, never the access hash from the URL. Called once per resolve of a document, which
   * is also where the once-only events start over — see {@link #trackOnce}.
   */
  setDocumentId(documentId: string): void {
    this.#documentId = documentId;
    this.#reportedOnce.clear();
  }

  /** Fires once per document load (per page open) — repeat opens are counted separately. */
  documentLoaded(documentType: string): void {
    this.#track('DocumentViewLoaded', { documentType });
  }

  documentLoadFailed(error: unknown): void {
    this.#track('DocumentViewLoadFailed', { reason: loadFailureReason(error), phase: 'documentLink' });
  }

  /**
   * The document loaded but its PDF never did, which is what the recipient sees as an empty preview. An event of its
   * own, not a `DocumentViewLoadFailed`: folded into that one it would be indistinguishable from a page that never
   * loaded at all. Reported from the preview helper through `DOCUMENT_PDF_TELEMETRY`, so the reason and the duration
   * are the request's own rather than the moment a notice happened to render.
   */
  pdfLoadFailed(error: unknown, durationMs: number): void {
    const status = httpStatusOf(error);

    this.#track('DocumentViewPdfFailed', {
      reason: loadFailureReason(error),
      durationMs: String(Math.round(durationMs)),
      ...(status === null ? {} : { status: String(status) }),
    });
  }

  /** Denominator for the header CTA clicks. */
  headerShown(variant: DocumentViewHeaderVariant): void {
    this.#track('DocumentViewHeaderShown', { variant });
  }

  /** Emitted for a hidden offer too — an offer nobody saw cannot convert. Counted once, see {@link #trackOnce}. */
  offerShown(shown: boolean): void {
    this.#trackOnce('DocumentViewOfferShown', { shown: String(shown) });
  }

  promoCtaClicked(): void {
    this.#track('DocumentViewPromoCtaClicked');
  }

  addToExpensesClicked(loggedIn: boolean): void {
    this.#track('DocumentViewAddToExpensesClicked', { loggedIn: String(loggedIn) });
  }

  importCompanyAddClicked(): void {
    this.#track('DocumentViewImportCompanyAddClicked');
  }

  headerTrialClicked(): void {
    this.#track('DocumentViewHeaderTrialClicked');
  }

  headerAppClicked(): void {
    this.#track('DocumentViewHeaderAppClicked');
  }

  headerLoginClicked(): void {
    this.#track('DocumentViewHeaderLoginClicked');
  }

  footerLinkClicked(): void {
    this.#track('DocumentViewFooterLinkClicked');
  }

  logoClicked(): void {
    this.#track('DocumentViewLogoClicked');
  }

  footerBrandClicked(): void {
    this.#track('DocumentViewFooterBrandClicked');
  }

  /** The share from the document tile is a `track-event` of the schema; only the primary actions report through here. */
  shareByEmailClicked(source: 'primaryActions'): void {
    this.#track('DocumentViewShareByEmailClicked', { source });
  }

  /**
   * One event of this name per resolve of a document, wherever it is reported from. A component that reports what it
   * showed is destroyed and re-created within one resolve, and a flag on the instance does not survive that — with one
   * in place, ~1 % of visits still sent `DocumentViewOfferShown` two to seven times (665545). This outlives the
   * component, so it holds whatever re-creates it; {@link setDocumentId} starts it over, so a document resolved again
   * reports again, the way `DocumentViewLoaded` does.
   */
  #trackOnce(name: string, properties?: Record<string, string>): void {
    if (this.#reportedOnce.has(name)) {
      return;
    }

    this.#reportedOnce.add(name);
    this.#track(name, properties);
  }

  #track(name: string, properties?: Record<string, string>): void {
    const documentId = this.#documentId;
    this.#appInsights.trackEvent(name, { ...(documentId === null ? {} : { documentId }), ...properties });
  }
}

function loadFailureReason(error: unknown): string {
  const status = (error as { status?: unknown } | null)?.status;
  if (typeof status === 'number') {
    return status === 0 ? 'network' : `HTTP ${status}`;
  }

  const name = (error as { name?: unknown } | null)?.name;
  if (name === 'TimeoutError') {
    return 'timeout';
  }
  if (name === 'TypeError' && isNetworkFailure(error)) {
    return 'network';
  }

  return typeof name === 'string' && name !== '' ? name : 'unknown';
}

/** What a request the network never completed says: Safari "Load failed", Chrome "Failed to fetch". */
function isNetworkFailure(error: unknown): boolean {
  const message = (error as { message?: unknown } | null)?.message;

  return typeof message === 'string' && (message.includes('Load failed') || message.includes('Failed to fetch'));
}

/** The status to filter on, where there is one; a request that never reached the server has none. */
function httpStatusOf(error: unknown): number | null {
  const status = (error as { status?: unknown } | null)?.status;

  return typeof status === 'number' && status !== 0 ? status : null;
}
